import { useEffect, useRef, useState } from "react";
import { useDashboardData } from "../../../context/DashboardContext";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { branding } from "../../../config/branding";
import type { WidgetComponentProps } from "../types";

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value);

export default function BarChartWidget({ config, title }: WidgetComponentProps) {
  const { chartData } = useDashboardData();
  const containerRef = useRef<HTMLDivElement | null>(null);

  const dataKey = config.dataKey as string;
  const valueKey = (config.valueKey as string) || "total";
  const xKey = (config.xKey as string) || "month";
  const barColor = (config.barColor as string) || branding.colors.chart[0];
  const height = (config.height as number) || 260;
  const [dimensions, setDimensions] = useState({ width: 0, height });

  const data = (chartData[dataKey] as Record<string, unknown>[] | undefined) || [];

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const update = () => {
      const nextWidth = Math.floor(element.clientWidth);
      const nextHeight = Math.max(height, Math.floor(element.clientHeight));
      setDimensions((prev) =>
        prev.width === nextWidth && prev.height === nextHeight
          ? prev
          : { width: nextWidth, height: nextHeight },
      );
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, [height]);

  if (data.length === 0) {
    return (
      <div className="bg-card rounded-xl border border-border p-6 h-full flex flex-col">
        <h3 className="text-base font-semibold mb-4">{title}</h3>
        <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
          No data available
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl border border-border p-3 sm:p-6 h-full flex flex-col overflow-hidden">
      <h3 className="text-sm sm:text-base font-semibold mb-3 sm:mb-5">{title}</h3>
      <div ref={containerRef} className="flex-1 min-w-0 min-h-0" style={{ minHeight: Math.min(height, 220) }}>
        {dimensions.width > 0 && dimensions.height > 0 ? (
          <BarChart width={dimensions.width} height={dimensions.height} data={data}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-border" />
            <XAxis dataKey={xKey} tick={{ fontSize: 11 }} className="text-muted-foreground" axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11 }} className="text-muted-foreground" axisLine={false} tickLine={false}
              tickFormatter={(v: number) => {
                if (v >= 1000000) return `$${(v / 1000000).toFixed(1)}M`;
                if (v >= 1000) return `$${(v / 1000).toFixed(0)}K`;
                return `$${v}`;
              }}
            />
            <Tooltip
              content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                return (
                  <div className="bg-card border border-border rounded-xl shadow-xl px-4 py-3 backdrop-blur-md">
                    <p className="text-sm font-medium text-muted-foreground mb-1">{String(label)}</p>
                    {payload.map((entry, i) => (
                      <p key={i} className="text-sm font-semibold" style={{ color: entry.color }}>
                        {String(entry.name)}: {formatCurrency(entry.value as number)}
                      </p>
                    ))}
                  </div>
                );
              }}
            />
            <Bar dataKey={valueKey} name={config.barName as string || "Total"} fill={barColor} radius={[6, 6, 0, 0]} barSize={24} />
          </BarChart>
        ) : null}
      </div>
    </div>
  );
}
